import React from 'react';
import { DemographicsData } from '../../types';
import useReveal from '../../hooks/useReveal';
import { BAR_ANIMATION_MS, REVEAL_DELAY_MS, REVEAL_DURATION_MS, toMs } from '../../constants/animation';

interface DemographicsChartProps {
  data: DemographicsData;
  clientId: string;
}

type BarEntry = {
  label: string;
  value: number;
};

const PRIMARY_BLUE = '#3E71C2';
const LIGHTER_BLUE = '#9BB9F0';
const BAR_BACKGROUND = '#ECEFF5';
const BADGE_BACKGROUND = '#D9E4FB';
const BAR_RADIUS = 30;

const formatNumber = (value: number) => {
  if (!Number.isFinite(value)) return '0';
  const rounded = Math.round((value + Number.EPSILON) * 10) / 10;
  return `${rounded}`.replace(/\.0$/, '');
};

const buildEntriesAllowZeros = (items: BarEntry[]): BarEntry[] =>
  items.map((item) => ({ ...item, value: Number.isFinite(item.value) ? item.value : 0 }));

const SUBHEADER_PRIMARY = 'BASELINE: Online Adult Population | CONTAINS: Engaged Audiences Data';
const SUBHEADER_SECONDARY = 'BY INDEX with minimum composition: 5%';
const SECONDARY_COLOR = '#6B778C';

const AudienceHeader: React.FC<{ targetableIds: string }> = ({ targetableIds }) => (
  <div className="bg-white rounded-2xl shadow-md border border-slate-200 p-6">
    <div className="flex items-center gap-3 mb-2 text-3xl font-black text-slate-900">
      <span role="img" aria-label="audience">📊</span>
      <span>Audience: {targetableIds}</span>
    </div>
    <div className="text-sm text-slate-600">{SUBHEADER_PRIMARY}</div>
    <div className="text-[11px] uppercase tracking-wide text-slate-400">{SUBHEADER_SECONDARY}</div>
  </div>
);

const VerticalBarCard: React.FC<{
  title: string;
  entries: BarEntry[];
  icon: string;
}> = ({ title, entries, icon }) => {
  const ordered = entries;
  const maxValue = Math.max(...ordered.map((entry) => entry.value), 1);
  const CHART_HEIGHT = 300;
  const { ref, isVisible } = useReveal<HTMLDivElement>({ threshold: 0.2, once: true });

  return (
    <div
      ref={ref}
      className="bg-white rounded-2xl shadow-md border border-slate-200 pt-6 px-6 pb-8"
      style={{
        transition: `transform ${toMs(REVEAL_DURATION_MS)} ease-out, opacity ${toMs(REVEAL_DURATION_MS)} ease-out`,
        transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
        opacity: isVisible ? 1 : 0.25,
        transitionDelay: isVisible ? toMs(REVEAL_DELAY_MS) : '0ms',
      }}
    >
      <div className="mb-5 space-y-1">
        <div className="flex items-center gap-2">
          <span className="text-2xl leading-none" aria-hidden>{icon}</span>
          <h3 className="text-xl font-semibold text-slate-900">{title}</h3>
        </div>
        <div className="text-sm text-slate-600">{SUBHEADER_PRIMARY}</div>
        <div className="text-[11px] uppercase tracking-wide text-slate-400">{SUBHEADER_SECONDARY}</div>
      </div>
      <div className="flex items-end gap-[10px]">
        {ordered.map((entry, idx) => {
          const scaledHeight = Math.max(entry.value / maxValue, 0) * (CHART_HEIGHT - 24);
          const fillHeight = entry.value > 0 ? Math.max(scaledHeight, 10) : 0;
          const transitionDelay = isVisible ? toMs(REVEAL_DELAY_MS + idx * 140) : '0ms';
          return (
            <div key={entry.label} className="flex-1 flex flex-col items-center gap-3">
              <div className="w-full flex-1 flex items-end" style={{ minHeight: CHART_HEIGHT }}>
                <div
                  className="relative w-full"
                  style={{ height: CHART_HEIGHT, background: BAR_BACKGROUND, borderRadius: `${BAR_RADIUS * 2}px ${BAR_RADIUS * 2}px 0 0` }}
                >
                  <div
                    className="absolute inset-x-0 bottom-0"
                    style={{
                      height: `${isVisible ? fillHeight : 0}px`,
                      background: entry.value > 0 ? PRIMARY_BLUE : 'transparent',
                      borderRadius: `${BAR_RADIUS * 2}px ${BAR_RADIUS * 2}px 0 0`,
                      transition: `height ${toMs(BAR_ANIMATION_MS)} ease-out`,
                      transitionDelay,
                    }}
                  />
                  <div className="absolute inset-x-0 bottom-0 pb-3 flex justify-center">
                    <span
                      className="text-base font-semibold"
                      style={{ color: entry.value > 0 ? '#FFFFFF' : SECONDARY_COLOR }}
                    >
                      {formatNumber(entry.value)}%
                    </span>
                  </div>
                </div>
              </div>
              <div className="text-center h-12 flex items-center justify-center">
                <p className="text-sm font-semibold text-slate-700 px-2 leading-snug">{entry.label}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const EmploymentSummary: React.FC<{ data: DemographicsData['employment'] }> = ({ data }) => {
  if (!data) return null;
  const { ref, isVisible } = useReveal<HTMLDivElement>({ threshold: 0.2, once: true });

  const rawEntries = [
    { label: 'Self-Employed', value: data.selfEmployed ?? 0 },
    { label: 'Full-Time', value: data.fullTime ?? 0 },
    { label: 'Part-Time', value: data.partTime ?? 0 },
    { label: 'Unemployed', value: data.unemployed ?? 0 },
    { label: 'Homemaker', value: data.homemaker ?? 0 },
    { label: 'Student', value: data.student ?? 0 },
    { label: 'Retired', value: data.retired ?? 0 },
  ];

  const entries = rawEntries.filter((e) => e.value > 0);

  if (entries.length === 0) return null;

  const primary = entries.reduce((best, entry) => (entry.value > best.value ? entry : best), entries[0]);
  const maxValue = Math.max(...entries.map((entry) => entry.value), 100);

  return (
    <div
      ref={ref}
      className="bg-white rounded-2xl shadow-md border border-slate-200 pt-6 px-6 pb-8"
      style={{
        transition: `transform ${toMs(REVEAL_DURATION_MS)} ease-out, opacity ${toMs(REVEAL_DURATION_MS)} ease-out`,
        transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
        opacity: isVisible ? 1 : 0.25,
        transitionDelay: isVisible ? toMs(REVEAL_DELAY_MS) : '0ms',
      }}
    >
      <div className="mb-5 space-y-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-2xl leading-none" aria-hidden>💼</span>
            <h3 className="text-xl font-semibold text-slate-900">Employment Status</h3>
          </div>
          <div className="text-sm text-slate-600">{SUBHEADER_PRIMARY}</div>
          <div className="text-[11px] uppercase tracking-wide text-slate-400">{SUBHEADER_SECONDARY}</div>
        </div>
        <div className="flex flex-col items-center gap-3">
          <div className="text-sm uppercase tracking-[0.12em] font-semibold" style={{ color: PRIMARY_BLUE }}>
            {primary.label}
          </div>
          <div className="flex items-center justify-center gap-10">
            <div className="text-center">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-[0.08em]">Index</p>
              <p className="text-3xl font-black text-slate-900">{formatNumber(data.index ?? data.fullTimeIndex ?? 0)}</p>
            </div>
            <div className="h-16 w-16 rounded-full flex items-center justify-center" style={{ background: BADGE_BACKGROUND }}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                className="h-8 w-8"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                style={{ color: PRIMARY_BLUE }}
              >
                <rect x="4" y="8" width="16" height="10" rx="1.5" />
                <path d="M9 8V6a3 3 0 0 1 6 0v2" />
              </svg>
            </div>
            <div className="text-center">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-[0.08em]">% Comp</p>
              <p className="text-3xl font-black text-slate-900">
                {formatNumber(data.compositionPrimary ?? primary.value)}%
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {entries.map((entry, idx) => {
          const barWidth = entry.value > 0 ? Math.min((entry.value / maxValue) * 100, 100) : 0;
          const baselinePercent = Math.min((100 / maxValue) * 100, 100);
          return (
            <div
              key={entry.label}
              className="grid grid-cols-[120px,1fr,60px] items-start gap-6 text-sm font-semibold text-slate-700"
            >
              <span className="pt-1.5">{entry.label}</span>
              <div
                className="relative h-6 pb-6"
                style={{ background: BAR_BACKGROUND, borderRadius: `${BAR_RADIUS}px` }}
              >
                <div
                className="absolute inset-y-0 left-0 rounded-full flex items-center justify-end pr-3 text-white text-sm font-semibold"
                style={{
                    width: `${isVisible ? barWidth : 0}%`,
                    background: entry.value > 0 ? (entry.label === 'Full-Time' ? LIGHTER_BLUE : PRIMARY_BLUE) : 'transparent',
                    transition: `width ${toMs(BAR_ANIMATION_MS)} ease-out`,
                    transitionDelay: isVisible ? toMs(REVEAL_DELAY_MS + idx * 140) : '0ms',
                }}
              >
                {entry.value > 0 ? formatNumber(entry.value) : ''}
              </div>
                <div
                  className="absolute inset-y-0 w-px border-l border-dashed"
                  style={{ left: `${baselinePercent}%`, borderColor: SECONDARY_COLOR }}
                  aria-hidden
                />
                <div
                  className="absolute text-[11px] font-semibold"
                  style={{
                    top: 'calc(100% + 0.4rem)',
                    left: `${baselinePercent}%`,
                    transform: 'translateX(-50%)',
                    color: SECONDARY_COLOR
                  }}
                >
                  100
                </div>
              </div>
              <span className="text-right text-slate-600 pt-1.5">{formatNumber(entry.value)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

const GenderPieCard: React.FC<{ data: DemographicsData['gender'] }> = ({ data }) => {
  const male = data?.male ?? 0;
  const female = data?.female ?? 0;
  const total = male + female;
  if (total <= 0) return null;

  const percentMale = total === 0 ? 0 : Math.round((male / total) * 100);
  const { ref, isVisible } = useReveal<HTMLDivElement>({ threshold: 0.2, once: true });
  const [animatedPercent, setAnimatedPercent] = React.useState(0);

  React.useEffect(() => {
    if (!isVisible) {
      setAnimatedPercent(0);
      return;
    }
    let frame = 0;
    const duration = 700;
    const start = performance.now();
    const target = percentMale;

    const step = (now: number) => {
      const elapsed = Math.min(1, (now - start) / duration);
      const eased = easeOutCubic(elapsed);
      setAnimatedPercent(eased * target);
      if (elapsed < 1) {
        frame = requestAnimationFrame(step);
      }
    };

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [isVisible, percentMale]);

  const animatedMale = Math.min(100, Math.max(0, animatedPercent));
  const animatedFemale = Math.max(0, Math.min(100, 100 - animatedMale));
  const maleDegrees = (animatedMale / 100) * 360;
  const maleLargeArc = maleDegrees > 180 ? 1 : 0;

  const maleX = 50 + 42 * Math.sin((maleDegrees * Math.PI) / 180);
  const maleY = 50 - 42 * Math.cos((maleDegrees * Math.PI) / 180);

  return (
    <div
      ref={ref}
      className="bg-white rounded-2xl shadow-md border border-slate-200 p-6 flex flex-col gap-6"
      style={{
        transition: `transform ${toMs(REVEAL_DURATION_MS)} ease-out, opacity ${toMs(REVEAL_DURATION_MS)} ease-out`,
        transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
        opacity: isVisible ? 1 : 0.25,
        transitionDelay: isVisible ? toMs(REVEAL_DELAY_MS) : '0ms',
      }}
    >
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span className="text-2xl leading-none" aria-hidden>🚻</span>
          <h3 className="text-xl font-semibold text-slate-900">Gender</h3>
        </div>
        <div className="text-sm text-slate-600">{SUBHEADER_PRIMARY}</div>
        <div className="text-[11px] uppercase tracking-wide text-slate-400">{SUBHEADER_SECONDARY}</div>
      </div>
      <div
        className="self-center relative h-60 w-60"
        style={{
          transition: `transform ${toMs(BAR_ANIMATION_MS)} ease-out`,
          transform: isVisible ? 'scale(1)' : 'scale(0.8)',
          transitionDelay: isVisible ? toMs(REVEAL_DELAY_MS) : '0ms',
        }}
      >
        <svg viewBox="0 0 100 100" className="h-full w-full">
          <circle cx="50" cy="50" r="42" fill={LIGHTER_BLUE} />
          {maleDegrees > 0 ? (
            <path
              d={`M50 8
                A42 42 0 ${maleLargeArc} 1 ${maleX} ${maleY}
                L50 50Z`}
              fill={PRIMARY_BLUE}
            />
          ) : null}
          <path d="M50 50m0-42a42 42 0 1 1 0 84a42 42 0 1 1 0-84" fill="none" stroke="white" strokeWidth="6" />
        </svg>
      </div>
      <div className="flex gap-8 text-base font-semibold text-slate-700 justify-center">
        <div className="flex items-center gap-2">
          <span className="h-3.5 w-3.5 rounded-full" style={{ background: PRIMARY_BLUE }} />
          <span>Male · {Math.round(animatedMale)}%</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="h-3.5 w-3.5 rounded-full" style={{ background: LIGHTER_BLUE }} />
          <span>Female · {Math.round(animatedFemale)}%</span>
        </div>
      </div>
    </div>
  );
};

export default function DemographicsChart({ data }: DemographicsChartProps) {
  const ageEntries = buildEntriesAllowZeros([
    { label: '18-24', value: data.ageGroups?.['18-24'] ?? 0 },
    { label: '25-34', value: data.ageGroups?.['25-34'] ?? 0 },
    { label: '35-44', value: data.ageGroups?.['35-44'] ?? 0 },
    { label: '45-49', value: data.ageGroups?.['45-49'] ?? 0 },
    { label: '50-54', value: data.ageGroups?.['50-54'] ?? 0 },
    { label: '55-64', value: data.ageGroups?.['55-64'] ?? 0 },
    { label: '65+', value: data.ageGroups?.['65+'] ?? 0 },
  ]);

  const educationEntries = buildEntriesAllowZeros([
    { label: 'No High School', value: data.education?.lessThanHighSchool ?? 0 },
    { label: 'High School', value: data.education?.highSchool ?? 0 },
    { label: 'Some College', value: data.education?.someCollege ?? 0 },
    { label: 'College Degree', value: data.education?.collegeDegree ?? 0 },
    { label: 'Post-grad Degree', value: data.education?.postGrad ?? 0 },
  ]);

  return (
    <div className="space-y-8">
      <AudienceHeader targetableIds={data.targetableIds} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <GenderPieCard data={data.gender} />
        <VerticalBarCard title="Age Group" entries={ageEntries} icon="🧑‍🧒" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <VerticalBarCard title="Education" entries={educationEntries} icon="🎓" />
        <EmploymentSummary data={data.employment} />
      </div>
    </div>
  );
}
